import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { VoiceRecorder } from "./voice-recorder";
import {
  fetchCheckinByToken,
  submitCheckin,
  CheckinApiError,
  CheckinData,
  CheckinResponseItem,
} from "../api/checkin";

type Step = "loading" | "welcome" | "prompt" | "review" | "submitting" | "done" | "error" | "expired";

export const CheckinPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [step, setStep] = useState<Step>("loading");
  const [checkin, setCheckin] = useState<CheckinData | null>(null);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [responses, setResponses] = useState<CheckinResponseItem[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isRecording, setIsRecording] = useState(false);

  useEffect(() => {
    if (!token) {
      setStep("error");
      setErrorMessage("Token manquant");
      return;
    }

    fetchCheckinByToken(token)
      .then((data) => {
        setCheckin(data);
        // Set language from check-in
        if (data.language && data.language !== i18n.language) {
          i18n.changeLanguage(data.language);
        }
        // Initialize empty responses
        setResponses(
          data.prompts.map((p) => ({
            promptKey: p.key,
            transcription: "",
            method: "voice" as const,
          }))
        );
        if (data.status === "completed" || data.status === "processed") {
          setStep("done");
        } else {
          setStep("welcome");
        }
      })
      .catch((err: unknown) => {
        if (err instanceof CheckinApiError && err.status === 410) {
          setStep("expired");
        } else {
          setStep("error");
          setErrorMessage(err instanceof Error ? err.message : "Erreur inconnue");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleTranscriptionChange = useCallback(
    (text: string, method: "voice" | "text") => {
      setResponses((prev) => {
        const updated = [...prev];
        if (updated[currentPromptIndex]) {
          updated[currentPromptIndex] = {
            ...updated[currentPromptIndex],
            transcription: text,
            method,
          };
        }
        return updated;
      });
    },
    [currentPromptIndex]
  );

  const handleNext = () => {
    if (checkin && currentPromptIndex < checkin.prompts.length - 1) {
      setCurrentPromptIndex((i) => i + 1);
    } else {
      setStep("review");
    }
  };

  const handleBack = () => {
    if (currentPromptIndex > 0) {
      setCurrentPromptIndex((i) => i - 1);
    } else {
      setStep("welcome");
    }
  };

  const handleSubmit = async () => {
    if (!checkin) return;
    setStep("submitting");
    setErrorMessage("");
    try {
      await submitCheckin(token, responses, checkin.language);
      setStep("done");
    } catch (err: unknown) {
      // If already submitted (409), treat as done
      if (err instanceof CheckinApiError && err.status === 409) {
        setStep("done");
        return;
      }
      setStep("review");
      setErrorMessage(err instanceof Error ? err.message : "Erreur lors de la soumission");
    }
  };

  const language = checkin?.language || i18n.language || "fr";

  // Loading
  if (step === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#00c4cc]/10 to-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00c4cc]" />
      </div>
    );
  }

  // Error
  if (step === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#00c4cc]/10 to-white p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="text-4xl mb-4">:(</div>
          <h2 className="text-xl font-semibold mb-2">{t("checkin.errorTitle")}</h2>
          <p className="text-gray-600">{errorMessage || t("checkin.errorGeneric")}</p>
        </Card>
      </div>
    );
  }

  // Expired
  if (step === "expired") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#00c4cc]/10 to-white p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="text-4xl mb-4">&#x23F3;</div>
          <h2 className="text-xl font-semibold mb-2">{t("checkin.expiredTitle")}</h2>
          <p className="text-gray-600">{t("checkin.expiredMessage")}</p>
        </Card>
      </div>
    );
  }

  // Done
  if (step === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#00c4cc]/10 to-white p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">{t("checkin.doneTitle")}</h2>
          <p className="text-gray-600">{t("checkin.doneMessage")}</p>
        </Card>
      </div>
    );
  }

  // Welcome
  if (step === "welcome") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#00c4cc]/10 to-white p-4">
        <Card className="max-w-md w-full p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#00c4cc] mb-2">
              {t("checkin.welcomeTitle")}
            </h1>
            <p className="text-gray-600">{t("checkin.welcomeSubtitle")}</p>
          </div>
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#00c4cc]/15 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#00c4cc] font-semibold text-sm">1</span>
              </div>
              <p className="text-gray-700">{t("checkin.step1")}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#00c4cc]/15 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#00c4cc] font-semibold text-sm">2</span>
              </div>
              <p className="text-gray-700">{t("checkin.step2")}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#00c4cc]/15 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#00c4cc] font-semibold text-sm">3</span>
              </div>
              <p className="text-gray-700">{t("checkin.step3")}</p>
            </div>
          </div>
          <Button
            onClick={() => {
              setCurrentPromptIndex(0);
              setStep("prompt");
            }}
            className="w-full bg-[#00c4cc] hover:bg-[#00adc0] text-white"
          >
            {t("checkin.start")}
          </Button>
        </Card>
      </div>
    );
  }

  // Submitting
  if (step === "submitting") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#00c4cc]/10 to-white p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00c4cc] mx-auto mb-4" />
          <p className="text-gray-600">{t("checkin.submitting")}</p>
        </Card>
      </div>
    );
  }

  // Review
  if (step === "review") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#00c4cc]/10 to-white p-4">
        <Card className="max-w-lg w-full p-8">
          <h2 className="text-xl font-semibold mb-4">{t("checkin.reviewTitle")}</h2>
          {errorMessage && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">
              {errorMessage}
            </div>
          )}
          <div className="space-y-4 mb-6">
            {checkin?.prompts.map((prompt, i) => (
              <div key={prompt.key} className="border rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t("checkin.question")} {i + 1}
                </p>
                <p className="text-gray-800 mb-2">{prompt.text}</p>
                <p className="text-gray-600 text-sm bg-gray-50 rounded p-2">
                  {responses[i]?.transcription || (
                    <span className="italic text-gray-400">
                      {t("checkin.noResponse")}
                    </span>
                  )}
                </p>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setCurrentPromptIndex(0);
                setStep("prompt");
                setErrorMessage("");
              }}
              className="flex-1"
            >
              {t("checkin.edit")}
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 bg-[#00c4cc] hover:bg-[#00adc0] text-white"
              disabled={responses.every((r) => !r.transcription)}
            >
              {t("checkin.submit")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Prompt step
  const currentPrompt = checkin?.prompts[currentPromptIndex];
  const isLastPrompt = checkin
    ? currentPromptIndex === checkin.prompts.length - 1
    : false;
  const hasResponse = responses[currentPromptIndex]?.transcription?.length > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#00c4cc]/10 to-white p-4">
      <Card className="max-w-md w-full p-8">
        {/* Progress */}
        <div className="flex gap-1 mb-6">
          {checkin?.prompts.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full ${
                i <= currentPromptIndex ? "bg-[#00c4cc]" : "bg-gray-200"
              }`}
            />
          ))}
        </div>

        {/* Prompt */}
        <div className="mb-6">
          <p className="text-sm text-[#00c4cc] font-medium mb-2">
            {t("checkin.question")} {currentPromptIndex + 1} /{" "}
            {checkin?.prompts.length}
          </p>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            {currentPrompt?.text}
          </h2>
          {currentPrompt?.hint && (
            <p className="text-sm text-gray-400">{currentPrompt.hint}</p>
          )}
        </div>

        {/* Voice recorder */}
        <div className="mb-6">
          <VoiceRecorder
            key={currentPromptIndex}
            language={language}
            onTranscriptionChange={handleTranscriptionChange}
            onProcessingChange={setIsRecording}
            initialText={responses[currentPromptIndex]?.transcription || ""}
            checkinToken={token}
          />
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleBack} disabled={isRecording} className="flex-1">
            {t("checkin.back")}
          </Button>
          <Button
            onClick={handleNext}
            disabled={isRecording}
            className="flex-1 bg-[#00c4cc] hover:bg-[#00adc0] text-white disabled:opacity-50"
          >
            {isLastPrompt ? t("checkin.review") : t("checkin.next")}
          </Button>
        </div>

        {/* Skip hint */}
        {!hasResponse && (
          <p className="text-xs text-gray-400 text-center mt-3">
            {t("checkin.skipHint")}
          </p>
        )}
      </Card>
    </div>
  );
};
